// ===========================================
// COMPONENTE: Filtros de Auditoria
// ===========================================

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Calendar } from 'lucide-react'
import { Search, Filter, X } from 'lucide-react'
import type { FiltrosAuditoria, AccionAuditoria, EntidadAuditoria } from '@/types/auditoria'
import { ACCIONES_OPTIONS, ENTIDADES_OPTIONS } from '@/types/auditoria'

interface AuditoriaFiltersProps {
  filtros: FiltrosAuditoria
  onFiltrosChange: (filtros: FiltrosAuditoria) => void
  onLimpiar: () => void
}

export function AuditoriaFilters({
  filtros,
  onFiltrosChange,
  onLimpiar,
}: AuditoriaFiltersProps) {
  const [busquedaLocal, setBusquedaLocal] = useState(filtros.busqueda || '')

  const handleBuscar = () => {
    onFiltrosChange({ ...filtros, busqueda: busquedaLocal || undefined })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBuscar()
    }
  }

  const tieneFiltros =
    filtros.accion ||
    filtros.entidad ||
    filtros.user_email ||
    filtros.fecha_desde ||
    filtros.fecha_hasta ||
    filtros.busqueda

  return (
    <div className="space-y-4">
      {/* Barra de busqueda */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por usuario o email..."
            value={busquedaLocal}
            onChange={(e) => setBusquedaLocal(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-9"
          />
        </div>
        <Button onClick={handleBuscar}>Buscar</Button>
      </div>

      {/* Filtros avanzados */}
      <div className="flex flex-wrap gap-4 items-end">
        {/* Accion */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Accion</Label>
          <Select
            value={filtros.accion || 'all'}
            onValueChange={(value) =>
              onFiltrosChange({
                ...filtros,
                accion: value === 'all' ? undefined : (value as AccionAuditoria),
              })
            }
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {ACCIONES_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Entidad */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Modulo</Label>
          <Select
            value={filtros.entidad || 'all'}
            onValueChange={(value) =>
              onFiltrosChange({
                ...filtros,
                entidad: value === 'all' ? undefined : (value as EntidadAuditoria),
              })
            }
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {ENTIDADES_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fecha Desde */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Desde</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={filtros.fecha_desde || ''}
              onChange={(e) =>
                onFiltrosChange({
                  ...filtros,
                  fecha_desde: e.target.value || undefined,
                })
              }
              className="pl-9 w-[160px]"
            />
          </div>
        </div>

        {/* Fecha Hasta */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Hasta</Label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={filtros.fecha_hasta || ''}
              onChange={(e) =>
                onFiltrosChange({
                  ...filtros,
                  fecha_hasta: e.target.value || undefined,
                })
              }
              className="pl-9 w-[160px]"
            />
          </div>
        </div>

        {/* Limpiar filtros */}
        {tieneFiltros && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setBusquedaLocal('')
              onLimpiar()
            }}
          >
            <X className="h-4 w-4 mr-1" />
            Limpiar
          </Button>
        )}
      </div>

      {/* Indicador de filtros activos */}
      {tieneFiltros && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>Filtros activos</span>
        </div>
      )}
    </div>
  )
}
